import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  hasValidationErrors,
  showErrorToast,
  showInfoToast,
  showSuccessToast,
  toRomanianDate,
} from '../../../../../common/global/helpers';
import Button from '../../../../../common/widgets/Button';
import Icons from '../../../../../common/widgets/Icons';
import MonthPicker from '../../../../../common/widgets/MonthPicker';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import { ErrorState, ErrorType, ValidationError } from '../../../models/Errors';
import { resources } from '../../../resources/resources';
import { employeeFilesService } from '../../../services/employee-files';
import CustomToastContent from './Components/CustomToastContent';

export default function EmployeesFilesPage() {
  const [date, setDate] = useState<Date>(new Date());
  const { t: translate } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const clearErrors = (field: keyof typeof errors) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const handleDownloadFiles = async () => {
    setIsLoading(true);
    clearErrors('null');
    const createZipResponse =
      await employeeFilesService.createEmployeesFilesZip(
        date.getFullYear(),
        date.getMonth() + 1,
      );
    if (createZipResponse.hasErrors) {
      if (createZipResponse.errorType === ErrorType.ValidationError) {
        const validationErrors: ValidationError[] = [] as ValidationError[];
        validationErrors.push({
          code: 'adminEmployeesFilesValidation',
          message: translate(resources.admin.employeesFiles.validation),
          severity: 'error',
          placeholders: {},
        } as ValidationError);
        setErrors(
          produce(d => {
            d['null'] = validationErrors;
          }),
        );
      } else {
        showErrorToast(
          translate(resources.admin.fileImport.validation.genericError),
        );
      }
    } else {
      showInfoToast(createZipResponse.data.message, true, undefined, 13000);

      const intervalId = setInterval(async () => {
        try {
          const response =
            await employeeFilesService.getEmployeesFilesZipStatusUrl(
              createZipResponse.data.archiveId,
            );

          if (response.hasErrors) {
            showErrorToast('Something went wrong!');
            clearInterval(intervalId);
          } else if (
            !response.hasErrors &&
            response.data &&
            response.data.isGenerated
          ) {
            showSuccessToast(
              '',
              true,
              undefined,
              undefined,
              true,
              <CustomToastContent
                archiveId={createZipResponse.data.archiveId}
                archiveName={`Alegeri angajati ${toRomanianDate(date)}`}
              />,
            );

            clearInterval(intervalId);
          }
        } catch (error) {
          clearInterval(intervalId);

          console.error('Eroare la generarea URL-ului de descărcare', error);
        }
      }, 15000);
    }
    setIsLoading(false);
  };

  const handlelDateChange = (e: DatePickerChangeEvent) => {
    setDate(e.value ?? new Date());
  };

  return (
    <>
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Warning}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: undefined,
          }))}
          showClose={true}
        />
      )}
      <div className="panel padding-xl employees-files-section">
        <h1>{translate(resources.admin.employeesFiles.title)}</h1>
        <div className="inline-form">
          <div className="inline-form-fields">
            <MonthPicker
              label={translate(
                resources.admin.employeesFiles.chooseMonthAndYear,
              )}
              value={date}
              onChange={handlelDateChange}
            />
          </div>
          <div className="inline-form-actions">
            <Button
              className="btn btn-solid btn-sm"
              onClick={handleDownloadFiles}
              icon={<Icons.Report />}
              loading={isLoading}
            >
              <span className="btn-text">
                {translate(resources.admin.employeesFiles.downloadZipFile)}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
